/**
 * Leave Policy Assistant — floating in-app chat widget.
 *
 * A launcher button pinned bottom-right on every authenticated page opens a
 * fixed-size chat panel that answers HR leave-policy questions. It is the web
 * transport for the same grounded Q&A the Slack Leave Assistant serves, via
 * `askAssistant` → `POST /api/v1/assistant/ask`.
 *
 * Conversation memory is server-side (Redis, ~1 week TTL): the panel restores
 * the stored thread on open (`getConversation`) and a "clear" action drops it
 * (`clearConversation`). The client still passes recent turns to `ask` as a
 * fallback for when the cache is cold/unavailable. Gated by
 * `flags.policyAssistant` at the mount site (AppShell).
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  askAssistant,
  clearConversation,
  getConversation,
  type AssistantTurn,
} from '../../api/assistant'
import { ApiError } from '../../api/client'
import { useHealthBar, type HealthBarBand } from '../../api/health-bar'
import { useReducedMotion } from '../../lib/use-reduced-motion'
import { AnalysisMarkdown } from '../primitives/AnalysisMarkdown'
import { Button } from '../primitives/Button'
import { Icon } from '../primitives/Icon'
import { persistCalloutDismissed, readCalloutDismissed } from './callout-dismissal'

interface ChatMessage {
  role: AssistantTurn['role']
  content: string
}

const PANEL_WIDTH = 360
// A bounce overshoots, so it runs a touch longer than the 150ms base transition;
// the exit uses the same budget before the panel unmounts.
const BOUNCE_MS = 240
// The greeting bubble gets out of the way if ignored. Unlike a dismissal this
// isn't remembered, so it shows again on the next load.
const CALLOUT_AUTO_HIDE_MS = 30_000
const _WELCOME =
  'Hi! Ask me anything about the leave policy, lah — entitlements, notice periods, ' +
  'documents needed, that kind of thing.'

export function PolicyAssistantWidget(): JSX.Element {
  const [open, setOpen] = useState(false)
  const [rendered, setRendered] = useState(false)
  const [closing, setClosing] = useState(false)
  const [draft, setDraft] = useState('')
  const [messages, setMessages] = useState<readonly ChatMessage[]>([])
  const [restored, setRestored] = useState(false)
  const [hovered, setHovered] = useState(false)
  // The greeting shows until the close button (or opening the chat) dismisses
  // it; the dismissal is remembered in this browser.
  const [calloutDismissed, setCalloutDismissed] = useState(readCalloutDismissed)
  const [calloutTimedOut, setCalloutTimedOut] = useState(false)
  const reduceMotion = useReducedMotion()
  // Tint the launcher by the employee's own leave-health band so the button
  // doubles as a glanceable outlook signal (green = healthy … red = high risk).
  const { data: health } = useHealthBar()
  const healthColor = healthBandColor(health?.band)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Restore the stored (Redis-backed) conversation the first time the panel opens.
  const convo = useQuery({
    queryKey: ['assistant-conversation'],
    queryFn: () => getConversation(),
    enabled: rendered && !restored,
    staleTime: Infinity,
  })
  useEffect(() => {
    if (!convo.data || restored) return
    setRestored(true)
    // Seed from the store only if the user hasn't already started typing while
    // the fetch was in flight — never clobber a live message with stale/empty data.
    if (convo.data.length > 0) {
      const restoredTurns = convo.data.map((t) => ({ role: t.role, content: t.content }))
      setMessages((prev) => (prev.length > 0 ? prev : restoredTurns))
    }
  }, [convo.data, restored])

  const ask = useMutation({
    // History is captured at send-time and passed as a variable, NOT closed over
    // from render state: React Query re-invokes the latest mutationFn closure
    // after the user-message state update re-renders, which would otherwise leak
    // the just-sent question into its own history.
    mutationFn: ({ question, history }: { question: string; history: AssistantTurn[] }) =>
      askAssistant(question, history),
    onSuccess: (answer) => {
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }])
    },
  })

  const clear = useMutation({
    mutationFn: () => clearConversation(),
    onSuccess: () => {
      setMessages([])
      ask.reset()
    },
  })

  useEffect(() => {
    if (calloutDismissed) return
    const timer = setTimeout(() => setCalloutTimedOut(true), CALLOUT_AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [calloutDismissed])

  const dismissCallout = (): void => {
    setCalloutDismissed(true)
    persistCalloutDismissed()
  }

  const openPanel = (): void => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setClosing(false)
    setRendered(true)
    setOpen(true)
    // Engaging with the chat implies they've found it — retire the greeting.
    dismissCallout()
  }

  const closePanel = (): void => {
    setOpen(false)
    if (reduceMotion) {
      setRendered(false)
      return
    }
    setClosing(true)
    closeTimer.current = setTimeout(() => {
      setRendered(false)
      setClosing(false)
    }, BOUNCE_MS)
  }

  // Move focus only on a real open↔close transition (input on open, launcher on
  // close). Comparing against the previous value — rather than a first-run flag —
  // means we never focus the launcher on mount, even under StrictMode's dev
  // double-invoke, so no focus ring appears on page load.
  const prevOpen = useRef(open)
  useEffect(() => {
    if (open && !prevOpen.current) inputRef.current?.focus()
    else if (!open && prevOpen.current) launcherRef.current?.focus()
    prevOpen.current = open
  }, [open])

  // Keep the newest message in view — as the conversation grows AND each time the
  // panel opens (it remounts on open, so its scroll box would otherwise start at
  // the top instead of the last message). rAF lets restored markdown lay out
  // before we measure scrollHeight.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
    return () => cancelAnimationFrame(id)
  }, [messages, ask.isPending, rendered])

  // Clear any pending unmount timer on teardown.
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  // Click outside the panel (and not on the launcher) closes the chat.
  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (panelRef.current?.contains(target) || dockRef.current?.contains(target)) {
        return
      }
      closePanel()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const send = (): void => {
    const question = draft.trim()
    if (!question || ask.isPending) return
    const history = messages.map((m) => ({ role: m.role, content: m.content }))
    setMessages((prev) => [...prev, { role: 'user', content: question }])
    setDraft('')
    ask.mutate({ question, history })
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends; Shift+Enter inserts a newline (standard chat affordance).
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closePanel()
    }
  }

  const errorText =
    ask.isError && ask.error instanceof ApiError
      ? ask.error.message
      : ask.isError
        ? 'Something went wrong. Please try again.'
        : null

  return (
    <>
      {rendered ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Leave Policy Assistant"
          onKeyDown={onPanelKeyDown}
          style={panelStyle(reduceMotion, closing)}
        >
          <header style={headerStyle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="support_agent" size={20} color="var(--accent)" />
              <strong style={{ fontSize: 14, color: 'var(--text)' }}>Leave Policy Assistant</strong>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {messages.length > 0 ? (
                <button
                  type="button"
                  aria-label="Clear conversation"
                  onClick={() => clear.mutate()}
                  disabled={clear.isPending}
                  style={iconButtonStyle}
                >
                  <Icon name="restart_alt" size={20} color="var(--text-2)" />
                </button>
              ) : null}
              <button
                type="button"
                aria-label="Close assistant"
                onClick={closePanel}
                style={iconButtonStyle}
              >
                <Icon name="close" size={20} color="var(--text-2)" />
              </button>
            </span>
          </header>

          <div ref={scrollRef} style={scrollStyle} aria-live="polite">
            <Bubble role="assistant" text={_WELCOME} />
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.content} />
            ))}
            {ask.isPending ? <ThinkingText reduceMotion={reduceMotion} /> : null}
            {errorText ? (
              <p role="alert" style={errorStyle}>
                {errorText}
              </p>
            ) : null}
          </div>

          <div style={composerStyle}>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Ask about leave policy…"
              rows={1}
              aria-label="Your question"
              style={textareaStyle}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={send}
              disabled={!draft.trim() || ask.isPending}
              aria-label="Send question"
            >
              <Icon name="send" size={16} />
            </Button>
          </div>
        </div>
      ) : null}

      <div ref={dockRef} style={launcherDockStyle}>
        {!open && !calloutDismissed && !calloutTimedOut ? (
          <div role="note" aria-label="Policy Assistant tip" style={calloutStyle(reduceMotion)}>
            <span>Need some guidance on leave? Feel free to ask me!</span>
            <button
              type="button"
              aria-label="Dismiss tip"
              onClick={dismissCallout}
              style={calloutCloseStyle}
            >
              <Icon name="close" size={14} color="var(--text-2)" />
            </button>
            <span aria-hidden="true" style={calloutTailStyle} />
          </div>
        ) : null}
        <button
          ref={launcherRef}
          type="button"
          aria-label="Open Leave Policy Assistant"
          aria-expanded={open}
          onClick={() => (open ? closePanel() : openPanel())}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setHovered(true)}
          onBlur={() => setHovered(false)}
          style={launcherStyle(open, hovered, reduceMotion, healthColor)}
        >
          <Icon name={open ? 'close' : 'support_agent'} size={26} color="#FFFFFF" />
        </button>
      </div>
    </>
  )
}

interface BubbleProps {
  role: ChatMessage['role']
  text: string
  muted?: boolean
}

function Bubble({ role, text, muted = false }: BubbleProps): JSX.Element {
  const isUser = role === 'user'
  // Assistant answers arrive as Markdown (**bold**, "- " bullets); render them.
  // The user's own text and transient status ("Thinking…") stay plain.
  const renderMarkdown = !isUser && !muted
  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        // Full content width so a wide reply's right edge lines up with the
        // right-aligned user bubble (both end at the container's right padding).
        maxWidth: '100%',
        padding: '8px 12px',
        borderRadius: 12,
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: renderMarkdown ? 'normal' : 'pre-wrap',
        background: isUser ? 'var(--accent)' : 'var(--surface-2)',
        color: isUser ? '#FFFFFF' : muted ? 'var(--text-3)' : 'var(--text)',
        border: isUser ? 'none' : '1px solid var(--border)',
      }}
    >
      {renderMarkdown ? <AnalysisMarkdown text={text} /> : text}
    </div>
  )
}

// Animated "thinking" line — no chip, just a highlight sweeping across the word.
function ThinkingText({ reduceMotion }: { reduceMotion: boolean }): JSX.Element {
  return (
    <div style={{ alignSelf: 'flex-start', padding: '2px 4px' }} aria-label="Thinking">
      <span style={thinkingTextStyle(reduceMotion)}>Thinking…</span>
    </div>
  )
}

function thinkingTextStyle(reduceMotion: boolean): CSSProperties {
  const base: CSSProperties = { fontSize: 13, fontWeight: 500 }
  if (reduceMotion) return { ...base, color: 'var(--text-3)' }
  return {
    ...base,
    // A highlight band riding over a muted base; animating background-position
    // sweeps it across the (background-clipped) glyphs.
    background:
      'linear-gradient(90deg, var(--text-3) 20%, var(--accent) 45%, var(--text-3) 70%)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    animation: 'lms-text-shimmer 1.4s linear infinite',
  }
}

// ── styles ────────────────────────────────────────────────────────────────

// Fixed dock anchoring the launcher (and its hover tooltip) bottom-right.
const launcherDockStyle: CSSProperties = {
  position: 'fixed',
  right: 20,
  bottom: 20,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
}

// Map the employee's leave-health band to a launcher tint. No band yet (loading
// / error / no data) falls back to the brand accent.
function healthBandColor(band: HealthBarBand | undefined): string {
  switch (band) {
    case 'high':
      return 'var(--danger)'
    case 'medium':
      return 'var(--warning)'
    case 'low':
      return 'var(--success)'
    default:
      return 'var(--accent)'
  }
}

function launcherStyle(
  open: boolean,
  hovered: boolean,
  reduceMotion: boolean,
  baseColor: string,
): CSSProperties {
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Slight top highlight + health-tinted base reads as a raised, lit surface.
    background: `radial-gradient(120% 120% at 30% 20%, #ffffff33, #ffffff00 45%), ${baseColor}`,
    // Layered soft shadow = real elevation; deepens on hover so it lifts further.
    boxShadow: hovered
      ? '0 18px 38px rgba(0, 0, 0, 0.32), 0 6px 14px rgba(0, 0, 0, 0.22)'
      : '0 12px 30px rgba(0, 0, 0, 0.28), 0 4px 10px rgba(0, 0, 0, 0.16)',
    transform: open
      ? 'scale(0.94)'
      : hovered
        ? 'translateY(-3px) scale(1.06)'
        : 'none',
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    // Gentle idle bob when idle+closed so it looks like it's floating. Hover
    // takes over with the lift transform; reduced-motion disables the bob.
    animation:
      !open && !hovered && !reduceMotion
        ? 'lms-float-bob 3.2s ease-in-out infinite'
        : undefined,
  }
}

// Comic-style greeting bubble sitting above the launcher, tail pointing down at
// it — a persistent, dismissable callout (not a hover tooltip).
function calloutStyle(reduceMotion: boolean): CSSProperties {
  return {
    position: 'absolute',
    bottom: 70,
    right: 0,
    width: 'max-content',
    maxWidth: 240,
    padding: '10px 30px 10px 14px',
    borderRadius: 16,
    background: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.20)',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    transformOrigin: 'bottom right',
    animation: reduceMotion ? undefined : 'lms-bounce-in 240ms ease both',
  }
}

const calloutCloseStyle: CSSProperties = {
  position: 'absolute',
  top: 4,
  right: 4,
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 6,
}

// A small rotated square poking out the bottom edge = the speech-bubble tail.
// Only its right + bottom borders show, so it matches the bubble's outline.
const calloutTailStyle: CSSProperties = {
  position: 'absolute',
  bottom: -6,
  right: 22,
  width: 12,
  height: 12,
  background: 'var(--surface)',
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  transform: 'rotate(45deg)',
}

function panelStyle(reduceMotion: boolean, closing: boolean): CSSProperties {
  return {
    position: 'fixed',
    right: 20,
    bottom: 88,
    width: PANEL_WIDTH,
    maxWidth: 'calc(100vw - 40px)',
    // Fixed height (was max-height) so the window doesn't grow/shrink with content.
    height: 'min(560px, calc(100vh - 120px))',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.22)',
    zIndex: 1000,
    overflow: 'hidden',
    // Scale from the launcher corner so the bounce reads as coming from the button.
    transformOrigin: 'bottom right',
    animation: reduceMotion
      ? undefined
      : `${closing ? 'lms-bounce-out' : 'lms-bounce-in'} ${BOUNCE_MS}ms ease both`,
  }
}

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  borderBottom: '1px solid var(--border)',
}

const scrollStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '14px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const composerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  borderTop: '1px solid var(--border)',
}

const textareaStyle: CSSProperties = {
  flex: 1,
  resize: 'none',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--text)',
  background: 'var(--surface)',
}

const iconButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 8,
}

const errorStyle: CSSProperties = {
  alignSelf: 'flex-start',
  fontSize: 12,
  color: 'var(--danger, #C0392B)',
  margin: 0,
}
